// CC3.MVP W3a · Portal auth surface (login / activate / reset-request / reset).
// Uses `aa_portal_token` namespaced storage ONLY. Internal AAC token
// storage (`aa_ops_token`) is never read or written by these pages.

import { useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const AA_PORTAL_TOKEN = "aa_portal_token";

// Dedicated axios instance for portal — never uses aa_ops_token.
export const portalHttp = axios.create({ baseURL: API });
portalHttp.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(AA_PORTAL_TOKEN);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function ErrorBanner({ msg }) {
  if (!msg) return null;
  return (
    <div
      role="alert"
      data-testid="portal-error-banner"
      className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
    >
      {msg}
    </div>
  );
}

function PortalShellCard({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#F7F8F9] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-lg bg-white shadow-sm border border-[#E8EBED] overflow-hidden">
        <div className="bg-[#111315] px-6 py-5">
          <div
            className="text-white font-semibold tracking-wide text-lg"
            style={{ fontFamily: "Montserrat, sans-serif" }}
            data-testid="portal-wordmark"
          >
            ALL ACCESS PORTAL
          </div>
          <div className="text-[#C7CDD2] text-xs mt-1">
            Client portal · authorized personnel only
          </div>
        </div>
        <div className="px-6 py-6">
          <h1 className="text-xl font-semibold text-[#111315] mb-1">{title}</h1>
          {subtitle && <p className="text-sm text-[#6B747C] mb-4">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

function humanError(e, fallback = "Request failed") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (d?.code) return d.code.replaceAll("_", " ");
  return e?.message || fallback;
}

export function PortalLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(loc.state?.notice || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await portalHttp.post("/portal/session/login", { email, password });
      localStorage.setItem(AA_PORTAL_TOKEN, r.data.access_token);
      // W3b will implement /portal/clients — for W3a, land on a
      // placeholder page so we can verify the token flip.
      nav("/portal/clients", { replace: true });
    } catch (e) {
      setErr(humanError(e, "Invalid email or password."));
    } finally { setBusy(false); }
  };

  return (
    <PortalShellCard title="Portal sign in" subtitle="Use the email your admin invited.">
      <ErrorBanner msg={err} />
      <form onSubmit={submit} className="space-y-3" data-testid="portal-login-form">
        <label className="block">
          <span className="block text-xs font-medium text-[#6B747C] mb-1">Email</span>
          <input
            type="email" required value={email} autoComplete="username"
            onChange={(e) => setEmail(e.target.value)}
            data-testid="portal-login-email"
            className="w-full rounded-md border border-[#C7CDD2] px-3 py-2 text-sm focus:border-[#1B4F8A] focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-[#6B747C] mb-1">Password</span>
          <input
            type="password" required value={password} autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            data-testid="portal-login-password"
            className="w-full rounded-md border border-[#C7CDD2] px-3 py-2 text-sm focus:border-[#1B4F8A] focus:outline-none"
          />
        </label>
        <button
          type="submit" disabled={busy}
          data-testid="portal-login-submit"
          className="w-full rounded-md bg-[#1B4F8A] hover:bg-[#0C2F55] disabled:bg-[#6B747C] text-white text-sm font-medium py-2.5 transition-colors"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <div className="text-center text-xs pt-2">
          <Link to="/portal/reset-request" data-testid="portal-forgot-link"
                className="text-[#1B4F8A] hover:underline">
            Forgot password?
          </Link>
        </div>
      </form>
    </PortalShellCard>
  );
}

function PasswordFields({ pwd, setPwd, confirm, setConfirm }) {
  return (
    <>
      <label className="block">
        <span className="block text-xs font-medium text-[#6B747C] mb-1">New password</span>
        <input
          type="password" required minLength={8} value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          data-testid="portal-password"
          className="w-full rounded-md border border-[#C7CDD2] px-3 py-2 text-sm focus:border-[#1B4F8A] focus:outline-none"
        />
        <span className="block text-[11px] text-[#6B747C] mt-1">
          Minimum 8 characters.
        </span>
      </label>
      <label className="block">
        <span className="block text-xs font-medium text-[#6B747C] mb-1">Confirm password</span>
        <input
          type="password" required minLength={8} value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid="portal-password-confirm"
          className="w-full rounded-md border border-[#C7CDD2] px-3 py-2 text-sm focus:border-[#1B4F8A] focus:outline-none"
        />
      </label>
    </>
  );
}

export function PortalActivate() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd !== confirm) return setErr("Passwords do not match.");
    if (pwd.length < 8) return setErr("Password must be at least 8 characters.");
    setBusy(true);
    try {
      await portalHttp.post(
        `/portal/session/activate/${encodeURIComponent(token || "")}`,
        { password: pwd }
      );
      setDone(true);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const map = {
        invalid_activation_token: "This activation link is invalid.",
        activation_expired: "This activation link has expired.",
        activation_already_consumed: "This activation link has already been used.",
        password_too_short: "Password must be at least 8 characters.",
      };
      setErr(map[code] || humanError(e, "Activation failed."));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <PortalShellCard title="Portal account activated" subtitle="You can now sign in.">
        <button
          onClick={() => nav("/portal/login", { replace: true })}
          data-testid="portal-activate-goto-login"
          className="w-full rounded-md bg-[#1B4F8A] hover:bg-[#0C2F55] text-white text-sm font-medium py-2.5"
        >
          Go to sign in
        </button>
      </PortalShellCard>
    );
  }

  return (
    <PortalShellCard title="Activate your portal account" subtitle="Set your password to continue.">
      <ErrorBanner msg={err} />
      <form onSubmit={submit} className="space-y-3" data-testid="portal-activate-form">
        <PasswordFields pwd={pwd} setPwd={setPwd} confirm={confirm} setConfirm={setConfirm} />
        <button
          type="submit" disabled={busy}
          data-testid="portal-activate-submit"
          className="w-full rounded-md bg-[#1B4F8A] hover:bg-[#0C2F55] disabled:bg-[#6B747C] text-white text-sm font-medium py-2.5"
        >
          {busy ? "Activating…" : "Activate account"}
        </button>
      </form>
    </PortalShellCard>
  );
}

export function PortalResetRequest() {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await portalHttp.post("/portal/session/reset-request", { email });
      setDone(true);
    } catch (e) {
      // Never disclose which emails do/don't exist. The route returns
      // 404 for missing/inactive; we still show the generic "check
      // with your admin" message rather than a specific error code.
      setDone(true);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <PortalShellCard title="Request received" subtitle="If a portal account exists, your admin can share the reset URL.">
        <div data-testid="portal-reset-request-done" className="text-sm text-[#6B747C]">
          Contact your All Access administrator for the reset URL.
        </div>
        <div className="mt-4">
          <Link
            to="/portal/login"
            data-testid="portal-reset-request-back-to-login"
            className="text-sm text-[#1B4F8A] hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </PortalShellCard>
    );
  }

  return (
    <PortalShellCard title="Reset your password" subtitle="Enter the email your admin invited.">
      <ErrorBanner msg={err} />
      <form onSubmit={submit} className="space-y-3" data-testid="portal-reset-request-form">
        <label className="block">
          <span className="block text-xs font-medium text-[#6B747C] mb-1">Email</span>
          <input
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="portal-reset-request-email"
            className="w-full rounded-md border border-[#C7CDD2] px-3 py-2 text-sm focus:border-[#1B4F8A] focus:outline-none"
          />
        </label>
        <button
          type="submit" disabled={busy}
          data-testid="portal-reset-request-submit"
          className="w-full rounded-md bg-[#1B4F8A] hover:bg-[#0C2F55] disabled:bg-[#6B747C] text-white text-sm font-medium py-2.5"
        >
          {busy ? "Submitting…" : "Request reset"}
        </button>
        <div className="text-center text-xs pt-1">
          <Link to="/portal/login" className="text-[#1B4F8A] hover:underline">
            Back to sign in
          </Link>
        </div>
      </form>
    </PortalShellCard>
  );
}

export function PortalReset() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd !== confirm) return setErr("Passwords do not match.");
    if (pwd.length < 8) return setErr("Password must be at least 8 characters.");
    setBusy(true);
    try {
      await portalHttp.post(
        `/portal/session/reset/${encodeURIComponent(token || "")}`,
        { password: pwd }
      );
      // Clear any stale portal token and force fresh sign-in.
      localStorage.removeItem(AA_PORTAL_TOKEN);
      nav("/portal/login", {
        replace: true,
        state: { notice: "Password updated. Please sign in with your new password." },
      });
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const map = {
        invalid_reset_token: "This reset link is invalid.",
        reset_expired: "This reset link has expired.",
        password_too_short: "Password must be at least 8 characters.",
      };
      setErr(map[code] || humanError(e, "Reset failed."));
    } finally { setBusy(false); }
  };

  return (
    <PortalShellCard title="Set a new password" subtitle="Use a password with at least 8 characters.">
      <ErrorBanner msg={err} />
      <form onSubmit={submit} className="space-y-3" data-testid="portal-reset-form">
        <PasswordFields pwd={pwd} setPwd={setPwd} confirm={confirm} setConfirm={setConfirm} />
        <button
          type="submit" disabled={busy}
          data-testid="portal-reset-submit"
          className="w-full rounded-md bg-[#1B4F8A] hover:bg-[#0C2F55] disabled:bg-[#6B747C] text-white text-sm font-medium py-2.5"
        >
          {busy ? "Updating…" : "Update password"}
        </button>
      </form>
    </PortalShellCard>
  );
}

// W3a shim — W3b will replace this with the real shell/picker.
export function PortalClientsPlaceholder() {
  const nav = useNavigate();
  const token = localStorage.getItem(AA_PORTAL_TOKEN);
  if (!token) { nav("/portal/login", { replace: true }); return null; }
  return (
    <PortalShellCard title="Signed in" subtitle="Portal client picker coming in W3b.">
      <div data-testid="portal-clients-placeholder" className="text-sm text-[#6B747C]">
        Authentication succeeded. The client list and workspace will land in W3b.
      </div>
      <button
        onClick={() => { localStorage.removeItem(AA_PORTAL_TOKEN); nav("/portal/login", { replace: true }); }}
        data-testid="portal-signout"
        className="mt-4 w-full rounded-md border border-[#C7CDD2] text-[#111315] text-sm py-2 hover:bg-[#F7F8F9]"
      >
        Sign out
      </button>
    </PortalShellCard>
  );
}
